import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { projectsAPI } from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';

const AdminProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [formData, setFormData] = useState({
    title: '',
    role: '',
    company: '',
    year: new Date().getFullYear(),
    category: 'Feature Films',
    description: '',
    order: 0,
  });
  const [mainImage, setMainImage] = useState(null);
  const [extraImages, setExtraImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [existingProject, setExistingProject] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await projectsAPI.getOne(id);
      const project = response.data;
      setExistingProject(project);
      setFormData({
        title: project.title,
        role: project.role,
        company: project.company,
        year: project.year,
        category: project.category,
        description: project.description || '',
        order: project.order,
      });
    } catch (error) {
      alert('Failed to load project');
      navigate('/admin');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('year', formData.year);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('order', formData.order);

      if (mainImage) {
        formDataToSend.append('main_image', mainImage);
      }

      if (isEdit) {
        await projectsAPI.update(id, formDataToSend);
        
        // Upload extra images if any
        if (extraImages.length > 0) {
          const imagesFormData = new FormData();
          extraImages.forEach(img => imagesFormData.append('images', img));
          await projectsAPI.addImages(id, imagesFormData);
        }

        // Upload video if any
        if (video) {
          const videoFormData = new FormData();
          videoFormData.append('video', video);
          await projectsAPI.addVideo(id, videoFormData);
        }

        alert('Project updated successfully!');
      } else {
        // For new projects, main_image is required
        if (!mainImage) {
          alert('Main image is required');
          setLoading(false);
          return;
        }

        const response = await projectsAPI.create(formDataToSend);
        const newProjectId = response.data.id;

        // Upload extra images if any
        if (extraImages.length > 0) {
          const imagesFormData = new FormData();
          extraImages.forEach(img => imagesFormData.append('images', img));
          await projectsAPI.addImages(newProjectId, imagesFormData);
        }

        // Upload video if any
        if (video) {
          const videoFormData = new FormData();
          videoFormData.append('video', video);
          await projectsAPI.addVideo(newProjectId, videoFormData);
        }

        alert('Project created successfully!');
      }

      navigate('/admin');
    } catch (error) {
      alert('Failed to save project');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8" data-testid="admin-project-form-page">
      <div className="container mx-auto px-6 max-w-3xl">
        <div className="mb-6">
          <Link to="/admin" className="text-blue-600 hover:underline" data-testid="back-to-admin">
            ← Back to Admin
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-6" data-testid="form-title">
            {isEdit ? 'Edit Project' : 'Add New Project'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"
                data-testid="project-title-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Role *
                </label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"
                  placeholder="e.g., VFX Supervisor"
                  data-testid="project-role-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"
                  placeholder="e.g., FRAMESTORE"
                  data-testid="project-company-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  required
                  min="1900"
                  max="2100"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"
                  data-testid="project-year-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"
                  data-testid="project-category-select"
                >
                  <option value="Feature Films">Feature Films</option>
                  <option value="TV">TV</option>
                  <option value="Others">Others</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Order (higher = shown first)
              </label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-black"
                data-testid="project-order-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description (optional)
              </label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => setFormData({...formData, description: value})}
                placeholder="Add a description to make this project clickable..."
                height="200px"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Main Image {!isEdit && '*'}
              </label>
              {existingProject && (
                <div className="mb-2">
                  <img
                    src={`${BACKEND_URL}${existingProject.main_image}`}
                    alt="Current main"
                    className="w-32 h-32 object-cover rounded"
                  />
                  <p className="text-sm text-gray-500 mt-1">Current image (upload new to replace)</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setMainImage(e.target.files[0])}
                required={!isEdit}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="project-main-image-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Extra Images (optional)
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setExtraImages(Array.from(e.target.files))}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="project-extra-images-input"
              />
              <p className="text-sm text-gray-500 mt-1">Multiple images allowed</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Video (optional)
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setVideo(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-black file:text-white hover:file:bg-gray-800"
                data-testid="project-video-input"
              />
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-black text-white py-3 px-6 rounded font-semibold hover:bg-gray-800 disabled:bg-gray-400"
                data-testid="project-submit-button"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Project' : 'Create Project'}
              </button>
              <Link
                to="/admin"
                className="flex-1 text-center bg-gray-200 text-gray-800 py-3 px-6 rounded font-semibold hover:bg-gray-300"
                data-testid="project-cancel-button"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminProjectForm;
